import {useEffect, useRef} from "react";
import {MenuItem, Select, TextField} from "@mui/material";
import {FormattedMessage, defineMessages} from "react-intl";
import {DateTime} from "luxon";

const options = ["thisMonth", "lastMonth", "custom", "all"] as const;
type OptionKey = (typeof options)[number];

const INTERVAL_OPTION_MESSAGES = defineMessages<OptionKey>({
    thisMonth: {id: "intervals.thisMonth", defaultMessage: "This month"},
    lastMonth: {id: "intervals.lastMonth", defaultMessage: "Last month"},
    custom: {id: "intervals.custom", defaultMessage: "Custom"},
    all: {id: "intervals.all", defaultMessage: "All"},
});
type DateRange = {from: Date; to: Date};
export type intervals = "thisMonth" | "lastMonth" | "all" | DateRange;
type intervalBase = {
    start: string;
    length: number;
};

const DateRangeWidget = ({
    range,
    setRange,
    disabled,
}: {
    range: DateRange;
    setRange: (v: DateRange) => void;
    disabled: boolean;
}) => {
    return (
        <>
            <TextField
                value={DateTime.fromJSDate(range.from).toISODate()}
                variant="outlined"
                onChange={(ev) => setRange({from: new Date(ev.target.value), to: range.to})}
                style={{marginLeft: "1em", marginRight: "0.5em"}}
                size="small"
                type="date"
                disabled={disabled}
                label={<FormattedMessage id="from" defaultMessage="From" />}
            />
            <TextField
                value={DateTime.fromJSDate(range.to).toISODate()}
                variant="outlined"
                onChange={(ev) => setRange({to: new Date(ev.target.value), from: range.from})}
                size="small"
                type="date"
                disabled={disabled}
                label={<FormattedMessage id="to" defaultMessage="To" />}
            />
        </>
    );
};

export function getRecordListInterval(interval: intervals): intervalBase {
    switch (interval) {
        case "thisMonth": {
            const thisMonth = DateTime.now().startOf("month");
            return {
                start: thisMonth.toISODate(),
                length: Math.ceil(thisMonth.endOf("month").diff(thisMonth).as("seconds")),
            };
        }
        case "lastMonth": {
            const prevMonth = DateTime.now().minus({months: 1}).startOf("month");
            return {
                start: prevMonth.toISODate(),
                length: Math.ceil(prevMonth.endOf("month").diff(prevMonth).as("seconds")),
            };
        }
        case "all":
            return null;
        default:
            return {
                start: DateTime.fromJSDate(interval.from).toISODate(),
                length: Math.ceil(
                    DateTime.fromJSDate(interval.to)
                        .endOf("day")
                        .diff(DateTime.fromJSDate(interval.from).startOf("day"))
                        .as("seconds"),
                ),
            };
    }
}

type Props = {
    interval: intervals;
    setInterval: (v: intervals) => void;
    disabled: boolean;
};

export const IntervalWidget = ({interval, setInterval, disabled}: Props) => {
    const lastCustomDate = useRef<DateRange>({from: new Date(), to: new Date()});

    useEffect(() => {
        if (typeof interval !== "string") lastCustomDate.current = interval;
    }, [interval]);

    const onChange = (val: string) => setInterval((val === "custom" ? lastCustomDate.current : val) as intervals);

    return (
        <div style={{display: "flex", alignItems: "center"}}>
            <Select
                value={typeof interval === "string" ? interval : "custom"}
                margin="dense"
                variant="outlined"
                onChange={(ev) => onChange(ev.target.value)}
                disabled={disabled}
            >
                {options.map((op) => (
                    <MenuItem key={op} value={op}>
                        <FormattedMessage {...INTERVAL_OPTION_MESSAGES[op]} />
                    </MenuItem>
                ))}
            </Select>
            {typeof interval !== "string" && (
                <DateRangeWidget range={interval} setRange={setInterval} disabled={disabled} />
            )}
        </div>
    );
};
